/** Sizing math for ProductTileImage; kept apart so the component file only exports components. */
/** Share of the tile the product should cover, and the most of either side it may take. */
const TARGET_AREA = 0.42;
const MAX_SIDE = 0.86;
/** Plain fit used before measuring and when a photo can't be read. */
export const DEFAULT_SCALE = 0.84;
export const SAMPLE = 64;

export type TileFit = { scale: number; x: number; y: number };

/**
 * Where the product sits in an RGBA sample of the photo, as a transform for an object-contain
 * image filling a square tile: translate (fractions of the tile) to center it, then scale.
 */
export function measureTileFit(data: Uint8ClampedArray | number[], width: number, height: number): TileFit | null {
  let x0 = width;
  let y0 = height;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4;
      const background = data[i + 3] < 16 || Math.min(data[i], data[i + 1], data[i + 2]) > 242;
      if (background) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return null;
  // object-contain: the photo's long side spans the tile.
  const unit = 1 / Math.max(width, height);
  const contentW = (x1 - x0 + 1) * unit;
  const contentH = (y1 - y0 + 1) * unit;
  const scale = Math.min(Math.sqrt(TARGET_AREA / (contentW * contentH)), MAX_SIDE / Math.max(contentW, contentH));
  const centerX = ((x0 + x1 + 1) / 2 - width / 2) * unit;
  const centerY = ((y0 + y1 + 1) / 2 - height / 2) * unit;
  return { scale: Math.min(Math.max(scale, 0.6), 2.6), x: -centerX, y: -centerY };
}

